import React, { useState, useEffect } from "react";
import { Activity } from "lucide-react";
import ActivitiesList from "../../activities/ActivitiesList";
import DeleteJobModal from "../DeleteJobModal";
import NewActivityModal, { ActivityData } from "../../activities/modals/NewActivityModal";
import api, { getUserUid } from "../../../../../utils/api";
import { API_ENDPOINTS } from "../../../../../config";

interface ActivitiesTabProps {
  jobId?: string;
  boardId?: string;
  jobTitle?: string;
  companyName?: string;
}

interface ActivityFromAPI {
  uid: string;
  title: string;
  type: string;
  description?: string | null;
  is_completed: boolean;
  start_date?: string | null;
  end_date?: string | null;
  job_uid?: string | null;
  contact_uid?: string | null;
  board_uid: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Activity {
  id: number;
  uid: string;
  title: string;
  company: string;
  position: string;
  category: string;
  status: string;
  timeAgo: string;
  note?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
  is_completed?: boolean;
  job_uid?: string | null;
  board_uid?: string;
}

const formatTimeAgo = (dateString?: string | null): string => {
  if (!dateString) return "Just now";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return "1 week ago";
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
};

const getCategoryFromType = (type: string): string => {
  const normalizedType = type.trim();
  
  if (normalizedType === "Apply") {
    return "Applications";
  }
  
  if (normalizedType === "Phone Screen" || normalizedType === "Phone Interview" || normalizedType === "On Site Interview") {
    return "Interviews";
  }
  
  if (normalizedType === "Offer Received" || normalizedType === "Accept Offer" || normalizedType === "Decline Offer") {
    return "Offers";
  }
  
  if (normalizedType === "Meeting" || normalizedType === "Networking Event") {
    return "Networking";
  }
  
  return "Other";
};

export default function ActivitiesTab({ jobId, boardId, jobTitle, companyName }: ActivitiesTabProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<number | null>(null);
  const [newActivityModalOpen, setNewActivityModalOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!boardId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await api.get<ActivityFromAPI[]>(API_ENDPOINTS.activities.getByKanban(boardId));
        
        const filteredData = jobId 
          ? data.filter(activity => activity.job_uid && String(activity.job_uid) === String(jobId))
          : data;
        
        const transformedActivities: Activity[] = filteredData.map((activity) => {
          const uidStr = activity.uid.replace(/-/g, '');
          const numericId = parseInt(uidStr.substring(0, 8), 16) % 1000000;
          
          return {
            id: numericId,
            uid: activity.uid,
            title: activity.title,
            company: companyName || "Unknown",
            position: jobTitle || "Unknown",
            category: getCategoryFromType(activity.type),
            status: activity.is_completed ? "Completed" : activity.type,
            timeAgo: formatTimeAgo(activity.created_at || activity.updated_at),
            note: activity.description || undefined,
            startDate: activity.start_date || undefined,
            endDate: activity.end_date || undefined,
            type: activity.type,
            is_completed: activity.is_completed,
            job_uid: activity.job_uid || null,
            board_uid: activity.board_uid,
          };
        });
        
        setActivities(transformedActivities);
      } catch (error) {

        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [boardId, jobId, jobTitle, companyName]);

  const handleDeleteActivity = (activityId: number) => {
    setActivityToDelete(activityId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (activityToDelete !== null) {
      setActivities(activities.filter(a => a.id !== activityToDelete));
      setActivityToDelete(null);
    }
    setDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setActivityToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleUpdateActivity = async (activityUid: string, updates: {
    title?: string;
    type?: string;
    description?: string | null;
    is_completed?: boolean;
    start_date?: string | null;
    end_date?: string | null;
  }) => {
    try {
      const activityPayload = {
        title: updates.title || '',
        type: updates.type || 'Other',
        description: updates.description || null,
        is_completed: updates.is_completed || false,
        start_date: updates.start_date || null,
        end_date: updates.end_date || null,
      };

      await api.post(API_ENDPOINTS.activities.update(activityUid), activityPayload);
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else if (error?.status === 404) {

      } else {

      }
      throw error;
    }
  };

  const handleCreateActivity = async (data: ActivityData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!data.board) {

        return;
      }

      if (!data.title?.trim()) {

        return;
      }

      const activityPayload = {
        title: data.title.trim(),
        type: data.category as any,
        description: data.note?.trim() || null,
        is_completed: data.completed || false,
        start_date: data.startDate ? new Date(data.startDate).toISOString() : null,
        end_date: data.endDate ? new Date(data.endDate).toISOString() : null,
        board_uid: data.board,
        job_uid: data.job || null,
        contact_uid: null,
        status: "active" as const,
      };

      await api.post(API_ENDPOINTS.activities.create, activityPayload);
      
      setNewActivityModalOpen(false);
      
      if (boardId) {
        const refreshedData = await api.get<ActivityFromAPI[]>(API_ENDPOINTS.activities.getByKanban(boardId));
        
        const filteredData = jobId 
          ? refreshedData.filter(activity => activity.job_uid && String(activity.job_uid) === String(jobId))
          : refreshedData;
        
        const transformedActivities: Activity[] = filteredData.map((activity) => {
          const uidStr = activity.uid.replace(/-/g, '');
          const numericId = parseInt(uidStr.substring(0, 8), 16) % 1000000;
          
          return {
            id: numericId,
            uid: activity.uid,
            title: activity.title,
            company: companyName || "Unknown",
            position: jobTitle || "Unknown",
            category: getCategoryFromType(activity.type),
            status: activity.is_completed ? "Completed" : activity.type,
            timeAgo: formatTimeAgo(activity.created_at || activity.updated_at),
            note: activity.description || undefined,
            startDate: activity.start_date || undefined,
            endDate: activity.end_date || undefined,
            type: activity.type,
            is_completed: activity.is_completed,
            job_uid: activity.job_uid || null,
            board_uid: activity.board_uid,
          };
        });
        
        setActivities(transformedActivities);
      }
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  if (loading) {
    return (
      <div className="activities-tab-wrapper">
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px', 
          color: '#6b7280', 
          fontSize: '14px' 
        }}>
          Loading activities...
        </div>
      </div>
    );
  }

  return (
    <div className="activities-tab-wrapper">
      <div className="activities-tab-header">
        <button
          className="btn-new-activity"
          onClick={() => setNewActivityModalOpen(true)}
          type="button"
        >
          + Activity
        </button>
      </div>

      {activities.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          color: '#6b7280', 
          fontSize: '14px' 
        }}>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
            <Activity size={48} style={{ color: '#9ca3af' }} />
          </div>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#374151' }}>No activities yet</div>
          <div>Click "+ Activity" to add your first activity</div>
        </div>
      ) : (
        <ActivitiesList 
          activities={activities}
          onDeleteActivity={handleDeleteActivity}
          onUpdateActivity={handleUpdateActivity}
        />
      )}

      <DeleteJobModal
        open={deleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Activity"
        message="Are you sure you want to delete this activity? This action cannot be undone."
        confirmText="Delete Activity"
      />

      <NewActivityModal
        open={newActivityModalOpen}
        onClose={() => setNewActivityModalOpen(false)}
        onCommit={handleCreateActivity}
        currentBoard={boardId || undefined}
        currentJob={jobId || undefined}
      />
    </div>
  );
}
